import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useGetIdentity, useList, useNavigation } from "@refinedev/core";
import {
  ArrowRight,
  CalendarPlus,
  Layers,
  Package,
  SearchSlashIcon,
  StoreIcon,
  TicketIcon,
  TrendingUp,
} from "lucide-react";

dayjs.extend(relativeTime);
import { Bar, BarChart, Label, Pie, PieChart, XAxis, YAxis } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContext,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useMembers } from "@/contexts/member-context";
import { Button } from "@/components/ui/button";
import { cn, getCityColor } from "@/lib/utils";
import { getStatusColor, getStatusHexColor } from "@/pages/orders/utils";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

/**
 * Chart wrapper that measures its container and passes explicit width/height
 * to Recharts children, bypassing the broken ResponsiveContainer in recharts alpha.
 */
function SizedChart({
  config,
  className,
  children,
}: {
  config: ChartConfig;
  className?: string;
  children: (size: { width: number; height: number }) => React.ReactNode;
}) {
  const uniqueId = useId();
  const chartId = `chart-${uniqueId.replace(/:/g, "")}`;
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const measure = useCallback(() => {
    if (ref.current) {
      const { width, height } = ref.current.getBoundingClientRect();
      setSize((prev) =>
        prev.width !== width || prev.height !== height
          ? { width, height }
          : prev,
      );
    }
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
          className,
        )}
      >
        <ChartStyle id={chartId} config={config} />
        {size.width > 0 && size.height > 0 && children(size)}
      </div>
    </ChartContext.Provider>
  );
}

function getGreeting(): string {
  const hour = dayjs().hour();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function Home() {
  const { members } = useMembers();
  const { data: identity } = useGetIdentity();
  const {
    query: { data: data },
  } = useList({
    resource: "events",
    pagination: {
      pageSize: 1,
      currentPage: 0,
    },
    filters: [
      {
        field: "createdAt",
        operator: "gte",
        value: dayjs().startOf("month").unix(),
      },
    ],
    meta: {
      facetBy: ["createdBy"],
      includeFields: ["id"],
    },
  });
  const {
    query: { data: orderData },
  } = useList({
    resource: "orders",
    pagination: {
      pageSize: 5,
      currentPage: 0,
    },
    filters: [
      {
        field: "createdAt",
        operator: "gte",
        value: dayjs().subtract(6, "month").unix(),
      },
    ],
    sorters: [
      {
        field: "createdAt",
        order: "desc",
      },
    ],
    meta: {
      facetBy: ["botStatus"],
      includeFields: [
        "id",
        "confirmationNumber",
        "status",
        "botStatus",
        "params",
        "createdAt",
      ],
    },
  });
  const { create, show, list } = useNavigation();

  const totalEventsThisMonth =
    data?.facets?.[0]?.counts?.reduce(
      (sum: number, c: any) => sum + c.count,
      0,
    ) ?? 0;

  const curatorData = data?.facets?.[0]?.counts?.reduce(
    (filtered: any[], c: any) => {
      const member = members.find(
        (m) =>
          m.id === c.value ||
          m.adminMetadata?.relatedIds?.includes(c.value),
      );

      if (member) {
        const idx = filtered.findIndex((m) => m.member.id === member.id);

        if (idx >= 0) {
          filtered[idx].count += c.count;
        } else {
          filtered.push({
            member,
            ...c,
            fill: `var(--color-${member.id})`,
          });
        }
      }
      return filtered;
    },
    [],
  );

  const topCurator = curatorData?.sort(
    (a: any, b: any) => b.count - a.count,
  )?.[0];

  const quickActions = [
    {
      label: "New Event",
      icon: CalendarPlus,
      onClick: () => create("events"),
      description: "Create a new event",
    },
    {
      label: "New Venue",
      icon: StoreIcon,
      onClick: () => create("venues"),
      description: "Add a venue",
    },
    {
      label: "Explore Events",
      icon: SearchSlashIcon,
      onClick: () => list("events"),
      description: "Browse all events",
    },
    {
      label: "View Orders",
      icon: Package,
      onClick: () => list("orders"),
      description: "Manage orders",
    },
  ];

  return (
    <div className="flex-1 space-y-6 px-4 pb-8 max-w-7xl mx-auto">
      {/* Hero greeting */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6">
        <div className="relative z-10">
          <p className="text-sm font-medium text-muted-foreground mb-1">
            {dayjs().format("dddd, MMMM D, YYYY")}
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            {getGreeting()},{" "}
            {(identity as any)?.displayName?.split(" ")?.[0] ?? "guest"}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Here's what's happening with Mood today.
          </p>
        </div>
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-primary/5 blur-2xl" />
        <div className="absolute -right-4 -bottom-10 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="relative overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Events This Month
            </CardTitle>
            <div className="rounded-md bg-primary/10 p-2">
              <TicketIcon className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalEventsThisMonth}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {dayjs().format("MMMM YYYY")}
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Orders
            </CardTitle>
            <div className="rounded-md bg-blue-500/10 p-2">
              <Layers className="h-4 w-4 text-blue-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {orderData?.total?.toString() ?? "0"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Last 6 months
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Top Curator
            </CardTitle>
            <div className="rounded-md bg-amber-500/10 p-2">
              <TrendingUp className="h-4 w-4 text-amber-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">
              {topCurator?.member?.displayName ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {topCurator ? `${topCurator.count} events` : "No data yet"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Curator chart - wider */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">Curator Leaderboard</CardTitle>
            <CardDescription>
              Events created in {dayjs().format("MMMM YYYY")} by team member
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data?.facets?.length > 0 && curatorData?.length > 0 ? (
              <SizedChart
                config={
                  curatorData?.reduce((acc: any, s: any) => {
                    acc[s.value] = {
                      label: s.member?.displayName ?? s.value,
                      color: getCityColor(s.value),
                    };
                    return acc;
                  }, {}) satisfies ChartConfig
                }
                className="h-[280px] w-full"
              >
                {({ width, height }) => (
                  <BarChart
                    width={width}
                    height={height}
                    accessibilityLayer
                    data={curatorData}
                    layout="vertical"
                  >
                    <XAxis type="number" dataKey="count" />
                    <YAxis
                      dataKey="member.displayName"
                      type="category"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      width={100}
                    />
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent hideLabel />}
                    />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} />
                  </BarChart>
                )}
              </SizedChart>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                No events created this month yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Orders pie chart - narrower */}
        <Card className="lg:col-span-2 flex flex-col">
          <CardHeader className="items-center pb-0">
            <CardTitle className="text-base">Order Status</CardTitle>
            <CardDescription>Distribution by bot status</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 pb-0">
            {orderData?.facets?.length > 0 ? (
              <SizedChart
                config={
                  orderData?.facets[0]?.counts?.reduce((acc: any, s: any) => {
                    acc[s.value] = {
                      label: s.value,
                      color: getStatusHexColor(s.value),
                    };
                    return acc;
                  }, {}) satisfies ChartConfig
                }
                className="h-[220px] w-full mx-auto"
              >
                {({ width, height }) => (
                  <PieChart width={width} height={height}>
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent hideLabel />}
                    />
                    <Pie
                      data={orderData?.facets[0]?.counts?.map(
                        (status: any) => ({
                          ...status,
                          fill: `var(--color-${status.value})`,
                        }),
                      )}
                      dataKey="count"
                      nameKey="value"
                      innerRadius={50}
                      strokeWidth={4}
                    >
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                            return (
                              <text
                                x={viewBox.cx}
                                y={viewBox.cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                              >
                                <tspan
                                  x={viewBox.cx}
                                  y={viewBox.cy}
                                  className="fill-foreground text-2xl font-bold"
                                >
                                  {orderData?.total}
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 20}
                                  className="fill-muted-foreground text-xs"
                                >
                                  Orders
                                </tspan>
                              </text>
                            );
                          }
                        }}
                      />
                    </Pie>
                  </PieChart>
                )}
              </SizedChart>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                No order data available
              </div>
            )}
          </CardContent>
          <CardFooter className="pb-4 pt-2 justify-center">
            <p className="text-xs text-muted-foreground">Last 6 months</p>
          </CardFooter>
        </Card>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Activity - takes 2 cols */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Recent Orders</CardTitle>
              <CardDescription>Latest order activity</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1"
              onClick={() => list("orders")}
            >
              View all
              <ArrowRight className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {Array.isArray(orderData?.data) && orderData.data.length ? (
              <div className="space-y-1">
                {orderData.data
                  .map((hit: any) => hit.document)
                  .map((o: any, i: number) => (
                    <div key={o.id}>
                      <div
                        className="flex items-center gap-3 py-3 px-2 rounded-lg transition-colors hover:bg-muted/50 cursor-pointer group"
                        onClick={() => show("orders", o.id)}
                      >
                        <div
                          className={cn(
                            "rounded-lg p-2 shrink-0",
                            o.status === "error" ||
                              o.status === "PaymentFailed" ||
                              o.status === "PurchaseError"
                              ? "bg-destructive/10"
                              : o.status === "PaymentCompleted" ||
                                  o.status === "PurchaseSucceeded" ||
                                  o.status === "Success"
                                ? "bg-green-500/10"
                                : "bg-muted",
                          )}
                        >
                          <Package
                            className={cn(
                              "h-4 w-4",
                              o.status === "error" ||
                                o.status === "PaymentFailed" ||
                                o.status === "PurchaseError"
                                ? "text-destructive"
                                : o.status === "PaymentCompleted" ||
                                    o.status === "PurchaseSucceeded" ||
                                    o.status === "Success"
                                  ? "text-green-500"
                                  : "text-muted-foreground",
                            )}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium font-mono leading-none truncate">
                            {o.confirmationNumber ?? "—"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {o.params?.email ?? "No email"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-1.5 py-0",
                              getStatusColor(o.status),
                            )}
                          >
                            {o.status}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-1.5 py-0",
                              getStatusColor(o.botStatus),
                            )}
                          >
                            {o.botStatus}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                          {dayjs
                            .unix(o.createdAt)
                            .fromNow()}
                        </span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </div>
                      {i <
                        (orderData.data as any[]).length - 1 && (
                        <Separator />
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                No recent orders
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick Actions</CardTitle>
            <CardDescription>Jump into common tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-2">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  className="flex items-center gap-3 rounded-lg border p-3 text-left transition-all hover:bg-muted/50 hover:shadow-sm group"
                  onClick={action.onClick}
                >
                  <div className="rounded-lg bg-primary/10 p-2.5 shrink-0 transition-colors group-hover:bg-primary/15">
                    <action.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{action.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {action.description}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
